<template>
  <div class="w-100 text-center py-5" v-if="isConfirming">
    <img src="../../assets/img/icon/new-success.png" width="110" class="mb-3">
    <p class="mb-1 heading-6-1 text-success">Payment successful</p>
    <p class="mb-3 body-2-2">Confirming your order, this only takes a moment.</p>
    <loader />
  </div>

  <XenditComponentsCheckout
    v-else-if="hasComponentsSdk"
    :tx_payment="tx_payment"
    @complete="handleComplete"
    @cancel="handleCancel"
    @expired="handleExpired">
  </XenditComponentsCheckout>

  <XenditInputCC
    v-else
    :tx_payment="tx_payment"
    :module_page="module_page"
    :submodule_page="submodule_page"
    v-on="$listeners">
  </XenditInputCC>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex';
  import { post } from '@/services/index.js';
  import XenditInputCC from '@/components/hotel/be/XenditInputCC';
  import XenditComponentsCheckout from '@/components/v2/XenditComponentsCheckout';
  import {
    buildSuccessRoute,
    buildCancelRoute,
    buildCancelEndpoint,
    shouldClearBeTxPaymentOnSuccess,
    shouldClearBeTxPaymentOnCancel,
    shouldClearBeTxPaymentOnExpired
  } from '@/utils/xenditPaymentFlow.js';

  // The backend only settles the Xendit webhook a few seconds after `session-complete`; leaving
  // straight away lands on a result page that still reads PENDING and renders nothing.
  const CONFIRM_GRACE_MS = 5000;

  export default {
    name: 'XenditCardPayment',
    components: {
      XenditInputCC,
      XenditComponentsCheckout
    },
    props: {
      tx_payment: {
        type: Object,
        default: () => ({})
      },
      module_page: {
        type: String,
        default: 'BOOKING_ENGINE'
      },
      submodule_page: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        isConfirming: false
      }
    },
    computed: {
      ...mapGetters(['getMerchant', 'getConciergeHomeAuth']),
      // The Sessions API sends this key only for merchants already migrated — the rest stay on Xendit.js.
      hasComponentsSdk() {
        return this.$isNotEmptyV2(this.tx_payment && this.tx_payment.components_sdk_key)
      },
      conciergeToken() {
        const auth = this.getConciergeHomeAuth
        return (auth && auth.token) || ''
      },
      flowContext() {
        return {
          module_page: this.module_page,
          submodule_page: this.submodule_page,
          tx_payment: this.tx_payment,
          merchant: this.getMerchant
        }
      }
    },
    created() {
      // Kept off `data` on purpose — the timer handle must stay non-reactive.
      this.confirmTimer = null
    },
    beforeDestroy() {
      if (this.confirmTimer) clearTimeout(this.confirmTimer)
    },
    methods: {
      ...mapMutations(['setBeTxPayment']),
      handleComplete() {
        const route = buildSuccessRoute(this.flowContext)

        // UPSELLING renders in place and has no result page — nothing to wait for.
        if (!route) {
          this.finishPayment(null)
          return
        }

        this.isConfirming = true
        this.confirmTimer = setTimeout(() => {
          this.confirmTimer = null
          this.finishPayment(route)
        }, CONFIRM_GRACE_MS)
      },
      // `done` is held back with the redirect: it clears the cart, and emptying it mid-wait would
      // blank the order summary still on screen.
      finishPayment(route) {
        this.$emit('done')

        if (shouldClearBeTxPaymentOnSuccess(this.flowContext)) this.setBeTxPayment({})

        if (route) this.$router.push(route)
      },
      handleCancel() {
        const route = buildCancelRoute(this.flowContext)
        const endpoint = buildCancelEndpoint(this.flowContext)

        // Reservation bill has no transaction to cancel, it just returns to the bill page.
        if (!endpoint) {
          this.$router.push(route)
          return
        }

        const apiParams = endpoint.auth_type
          ? [endpoint.api_module, endpoint.api_url, {}, { type: endpoint.auth_type, token: this.conciergeToken }]
          : [endpoint.api_module, `${endpoint.api_url}?merchant_id=${this.getMerchant.id}`]

        post.apply(null, apiParams)
          .then(() => this.leaveAfterCancel(route))
          .catch(() => this.leaveAfterCancel(route))
      },
      handleExpired() {
        if (shouldClearBeTxPaymentOnExpired(this.flowContext)) this.setBeTxPayment({})
        this.$emit('expired')
      },
      leaveAfterCancel(route) {
        this.$emit('done-destroyed')
        if (shouldClearBeTxPaymentOnCancel(this.flowContext)) this.setBeTxPayment({})
        if (route) this.$router.push(route)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/v2/XenditCardPayment.vue